import * as React from 'react';
import { useEffect } from 'react';
import { setInteractionModality, useCollectionState, useId, useItem, useListBoxSection } from '@sail/react-aria';
import { assignProps, view, createViewConfig } from '@sail/engine';
import { chevronDown } from '@sail/icons/react/Icon';
import { Checkbox } from './Checkbox';
import { Counter } from './Counter';
import { Header } from './Header';
import { Icon } from './Icon';
import useFauxTreeState from '../hooks/useFauxTreeState';
import { Label } from './Label';
export const FauxTreeGroupTransforms = {
    idField: 'id',
    makeGenericItem (props) {
        var _props_textValue;
        return {
            type: 'section',
            'aria-label': props['aria-label'],
            textValue: typeof props.title === 'string' ? props.title : (_props_textValue = props.textValue) !== null && _props_textValue !== void 0 ? _props_textValue : ''
        };
    }
};
export const FauxTreeGroupConfig = createViewConfig({
    name: 'FauxTreeGroup',
    props: {},
    defaults: {
        counterEnabled: true
    }
});
export const FauxTreeGroup = FauxTreeGroupConfig.createView(function FauxTreeGroup({ id, children, title, subviews, counterEnabled, groupSelectionAllEnabled, ...props }) {
    const headerRef = React.useRef(null);
    const key = useItem(id, FauxTreeGroupTransforms.makeGenericItem({
        'aria-label': props['aria-label'],
        title
    }));
    const { itemProps, headingProps: { id: headingId }, groupProps } = useListBoxSection({
        heading: title,
        'aria-label': props['aria-label']
    });
    const state = useCollectionState();
    const tree = state;
    const isOpen = !tree.expandable || tree.expandedKeys.has(key);
    const { selectGroupItems, isMultipleSelection } = useFauxTreeState();
    var _selectGroupItems_get;
    const selectGroupItem = (_selectGroupItems_get = selectGroupItems.get(key)) !== null && _selectGroupItems_get !== void 0 ? _selectGroupItems_get : {
        selectedCount: 0,
        totalCount: 0,
        selectedForeignCount: 0,
        checkableKeys: [],
        uncheckableKeys: [],
        isDisabled: false
    };
    const { selectedCount, totalCount, selectedForeignCount, checkableKeys, uncheckableKeys, isDisabled } = selectGroupItem;
    const areAllSelected = totalCount === selectedCount;
    const areAllEnabledSelected = checkableKeys.length - selectedForeignCount === selectedCount;
    const hasItemsSelected = selectedCount > 0;
    const groupId = useId();
    const triggerProps = {
        role: 'button',
        'aria-expanded': isOpen,
        'aria-controls': groupId
    };
    const focusedItem = state.collection.getItem(state.selectionManager.focusedKey);
    useEffect(()=>{
        if (focusedItem) {
            // expand all parents of the focused item
            let { parentKey } = focusedItem;
            const keysToExpand = new Set();
            while(parentKey){
                keysToExpand.add(parentKey);
                const parent = tree.collection.getItem(parentKey);
                parentKey = parentKey === (parent === null || parent === void 0 ? void 0 : parent.parentKey) ? undefined : parent === null || parent === void 0 ? void 0 : parent.parentKey;
            }
            if (Array.from(keysToExpand).some((key)=>!tree.expandedKeys.has(key))) {
                tree.setExpandedKeys(new Set([
                    ...keysToExpand,
                    ...tree.expandedKeys
                ]));
                setInteractionModality('keyboard');
            }
        }
    }, [
        tree,
        focusedItem
    ]);
    // TODO: Sail Checkbox has a visual bug in Chrome when the values of indeterminate and checked are modified in the same render.
    // This is a workaround to avoid it, but this bug must be fixed directly in Checkbox
    const [indeterminate, setIndeterminate] = React.useState(hasItemsSelected && !areAllSelected);
    React.useEffect(()=>{
        setIndeterminate(hasItemsSelected && !areAllSelected);
    }, [
        hasItemsSelected,
        areAllSelected
    ]);
    // TODO: Implement disable for groups
    return /*#__PURE__*/ React.createElement(view.div, {
        ...props,
        ...itemProps
    }, title ? /*#__PURE__*/ React.createElement(Header, {
        id: headingId,
        role: "presentation",
        inherits: subviews.header,
        onClick: ()=>{
            if (tree.expandable && !isMultipleSelection) {
                tree.selectionManager.setFocusedKey(null);
                tree.toggleKey(key);
            }
        },
        onMouseDown: (e)=>{
            var _headerRef_current;
            // Prevent focus going to the FauxTree when clicking the section header.
            if ((_headerRef_current = headerRef.current) === null || _headerRef_current === void 0 ? void 0 : _headerRef_current.contains(e.target)) {
                e.preventDefault();
            }
        },
        uses: [
            assignProps(tree.expandable ? triggerProps : {})
        ],
        css: {
            alignItems: 'flex-start',
            cursor: tree.expandable && !isMultipleSelection ? 'pointer' : 'default'
        },
        ref: headerRef
    }, !isMultipleSelection ? /*#__PURE__*/ React.createElement(React.Fragment, null, tree.expandable ? /*#__PURE__*/ React.createElement(Icon, {
        size: "xsmall",
        icon: chevronDown,
        css: {
            transition: 'transform 150ms',
            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            marginX: 'xxsmall'
        }
    }) : null, title) : /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(view.div, {
        css: {
            stack: 'x',
            gap: 'space.75',
            alignItems: 'center'
        }
    }, /*#__PURE__*/ React.createElement(view.div, {
        css: {
            stack: 'x',
            gap: 'space.75',
            alignItems: 'center',
            minHeight: 'space.250'
        }
    }, groupSelectionAllEnabled ? /*#__PURE__*/ React.createElement(Checkbox, {
        indeterminate: indeterminate,
        tabIndex: -1,
        label: title,
        subviews: {
            label: {
                css: {
                    marginLeft: '-2px',
                    font: 'heading.xsmall',
                    color: 'form',
                    cursor: !isDisabled ? 'pointer' : 'default'
                }
            },
            input: {
                css: {
                    ':focus': {
                        focusRing: 'none'
                    }
                }
            }
        },
        checked: areAllSelected,
        disabled: isDisabled,
        onChange: ()=>{
            if (areAllEnabledSelected) {
                state.selectionManager.setSelectedKeys(uncheckableKeys);
            } else {
                state.selectionManager.setSelectedKeys(checkableKeys);
            }
        }
    }) : /*#__PURE__*/ React.createElement(Label, {
        css: {
            font: 'heading.xsmall',
            color: 'form'
        }
    }, title)), selectedCount > 0 && counterEnabled ? /*#__PURE__*/ React.createElement(Counter, {
        "data-testid": `region-count-${id}`
    }, selectedCount) : null), tree.expandable ? /*#__PURE__*/ React.createElement(Icon, {
        size: "xsmall",
        icon: chevronDown,
        css: {
            transition: 'transform 150ms',
            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            marginX: 'xxsmall',
            cursor: 'pointer'
        },
        onClick: ()=>{
            tree.selectionManager.setFocusedKey(null);
            tree.toggleKey(key);
        }
    }) : null)) : null, /*#__PURE__*/ React.createElement("section", {
        role: "region",
        id: id,
        ...groupProps
    }, isOpen ? children : null));
}, {
    css: {
        padding: 'xsmall',
        bleedX: 'xsmall',
        borderWidth: '0',
        borderTopWidth: 'small',
        borderStyle: 'solid',
        borderColor: 'border',
        ':first-of-type': {
            borderTopWidth: '0',
            bleedTop: 'xsmall'
        },
        ':last-of-type': {
            bleedBottom: 'xsmall'
        }
    },
    subviews: {
        header: {
            css: {
                stack: 'x',
                gap: 'space.75',
                distribute: 'packed',
                font: 'heading.xsmall',
                color: 'form',
                paddingY: 'space.75',
                paddingX: 'space.125',
                minHeight: 'xlarge',
                userSelect: 'none'
            }
        },
        list: {
            css: {
                stack: 'y'
            }
        }
    }
});

//# sourceMappingURL=FauxTreeGroup.js.map